import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Loader2, Download, School, HeartHandshake, MessageSquare, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "school",  label: "Schools",  icon: School,          endpoint: "school",  fmt: "yyyy-mm-dd" },
  { key: "parent",  label: "Parents",  icon: HeartHandshake,  endpoint: "parent",  fmt: "yyyy-mm-dd" },
  { key: "contact", label: "Contact",  icon: MessageSquare,   endpoint: "contact", fmt: "yyyy-mm-dd" },
];

const COLUMNS = {
  school: [
    { key: "created_at", label: "Received", type: "date" },
    { key: "school_name", label: "School" },
    { key: "contact_person", label: "Contact" },
    { key: "email", label: "Email", type: "email" },
    { key: "phone", label: "Phone", type: "phone" },
    { key: "number_of_students", label: "Students" },
    { key: "preferred_visit_period", label: "Period" },
    { key: "message", label: "Message", wrap: true },
  ],
  parent: [
    { key: "created_at", label: "Received", type: "date" },
    { key: "parent_name", label: "Name" },
    { key: "email", label: "Email", type: "email" },
    { key: "phone", label: "Phone", type: "phone" },
    { key: "child_school", label: "Child's school" },
    { key: "child_age", label: "Age" },
    { key: "message", label: "Message", wrap: true },
  ],
  contact: [
    { key: "created_at", label: "Received", type: "date" },
    { key: "name", label: "Name" },
    { key: "organisation", label: "Organisation" },
    { key: "email", label: "Email", type: "email" },
    { key: "phone", label: "Phone", type: "phone" },
    { key: "enquiry_type", label: "Type" },
    { key: "message", label: "Message", wrap: true },
  ],
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (_e) {
    return iso;
  }
};

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (rows, columns, filename) => {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(c.type === "date" ? fmtDate(r[c.key]) : r[c.key])).join(","))
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const AdminEnquiries = () => {
  if (typeof document !== "undefined") document.title = "Enquiries — School Smiles Australia";
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("school");
  const [data, setData] = useState({ school: null, parent: null, contact: null });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, c] = await Promise.all([
        axios.get(`${API}/enquiries/school`, { withCredentials: true }),
        axios.get(`${API}/enquiries/parent`, { withCredentials: true }),
        axios.get(`${API}/enquiries/contact`, { withCredentials: true }),
      ]);
      setData({ school: s.data, parent: p.data, contact: c.data });
    } catch (err) {
      toast.error("Could not load enquiries. Please sign in again.");
      if (err.response?.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F9FF]" data-testid="admin-enquiries-page">
      {/* Top bar */}
      <header className="bg-white border-b border-[#E5F0FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden md:inline text-sm text-[#4B6B86]">Admin dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-[#4B6B86]">Signed in as <span className="font-medium text-[#14324A]">{user?.email}</span></span>
            <Link to="/" className="text-sm text-[#1677C8] hover:text-[#14324A] font-medium">View site</Link>
            <button onClick={onLogout} className="inline-flex items-center gap-2 text-sm font-medium text-[#14324A] hover:text-[#1677C8]" data-testid="admin-logout">
              <LogOut size={16} strokeWidth={2.5}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-bold text-3xl md:text-4xl text-[#14324A]">Enquiries</h1>
            <p className="mt-1 text-sm text-[#4B6B86]">All submissions from the website, newest first.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="btn-outline text-sm py-2 px-4" data-testid="admin-refresh">
              <RefreshCw size={16} strokeWidth={2.5} className={loading ? "animate-spin" : ""}/> Refresh
            </button>
            <button
              onClick={() => data[active] && downloadCsv(data[active], COLUMNS[active], `enquiries-${active}.csv`)}
              disabled={!data[active] || data[active].length === 0}
              className="btn-primary text-sm py-2 px-4 disabled:opacity-50"
              data-testid="admin-export-csv"
            >
              <Download size={16} strokeWidth={2.5}/> Export CSV
            </button>
          </div>
        </div>

        <Tabs value={active} onValueChange={setActive} className="w-full">
          <TabsList className="bg-white border border-[#E5F0FA] p-1 rounded-full h-auto">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-[#1677C8] data-[state=active]:text-white"
                data-testid={`admin-tab-${t.key}`}
              >
                <t.icon size={16} strokeWidth={2.5} className="mr-2"/>
                {t.label}
                {data[t.key] && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-5 rounded-full bg-[#F4F9FF] text-[#1677C8] text-xs font-semibold px-1 data-[state=active]:bg-white data-[state=active]:text-[#1677C8]">
                    {data[t.key].length}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-6">
              <EnquiryTable
                rows={data[t.key]}
                loading={loading}
                columns={COLUMNS[t.key]}
                emptyLabel={`No ${t.label.toLowerCase()} enquiries yet.`}
                testId={`admin-table-${t.key}`}
              />
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
};

const EnquiryTable = ({ rows, loading, columns, emptyLabel, testId }) => {
  if (loading && rows == null) {
    return (
      <div className="bg-white rounded-3xl border border-[#E5F0FA] p-12 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#1677C8]" size={28} />
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-[#E5F0FA] p-12 text-center text-[#4B6B86]" data-testid={`${testId}-empty`}>
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="bg-white rounded-3xl border border-[#E5F0FA] overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-[#F4F9FF]">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left font-semibold text-[#14324A] px-4 py-3 whitespace-nowrap">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5F0FA] align-top hover:bg-[#F4F9FF]/40">
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.wrap ? "max-w-xs" : "whitespace-nowrap"}`}>
                    {c.type === "date" && <span className="text-[#4B6B86]">{fmtDate(r[c.key])}</span>}
                    {c.type === "email" && (
                      <a href={`mailto:${r[c.key]}`} className="text-[#1677C8] hover:text-[#14324A]">{r[c.key]}</a>
                    )}
                    {c.type === "phone" && (
                      <a href={`tel:${(r[c.key] || "").replace(/\s+/g, "")}`} className="text-[#1677C8] hover:text-[#14324A]">{r[c.key]}</a>
                    )}
                    {!c.type && (
                      <span className={c.wrap ? "block whitespace-pre-wrap text-[#14324A]" : "text-[#14324A]"}>
                        {r[c.key] || "—"}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminEnquiries;
